import { Button, Input, Table, Pagination } from 'app/components';
import styles from './PortDetails.module.scss';

import { useGetArrivalsCsvMutation, useGetPortArrivalsDataMutation } from 'app/api/facade';
import { shipCallsColumns } from './PortDetailsTableColumns';
import { t } from 'i18next';
import { useState, useMemo, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import { throttle } from 'app/helpers';
import { useAppSelector } from 'app/store/store';
import { useParams } from 'react-router-dom';
import { Icon } from 'app/components/Icon';

import { Typo } from 'app/components/Typo';
import {
  DateTimeInterval,
  PortArrivalsFilter,
  PortArrivalsOrderParams,
  TargetArrivalsSearchRequest,
} from 'ais-facade-api/api';
import { useDebounce, useOnClickOutside } from 'app/hooks';
import { PortArrivalsFilterPanel } from 'app/components/FilterPanel';
import moment from 'moment';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { handleFileDownload } from "../../helpers/handleFileDownload";
import { getI18n } from 'react-i18next';
export interface PortArrivalsFilters extends PortArrivalsFilter {
  touched?: boolean;
  typeAll: boolean;
  countryAll: boolean;
  fleetGroupAll: boolean;
  term?: string;
}
export interface PortArrivalsSortingInterface {
  direction: PortArrivalsOrderParams['direction'] | 'default';
  by: PortArrivalsOrderParams['by'];
}

const initialSort: PortArrivalsOrderParams = {
  direction: 'ASC',
  by: 'ETA',
};

export const PortArrivals = () => {
  const language = getI18n().language;
  const { portKey } = useParams();
  const { addAlert } = useAlertHook();

  const user = useAppSelector((state) => state.userSlice.user);

  const [getCSV] = useGetArrivalsCsvMutation();

  const curDate = useMemo(() => moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'), []);
  const next24h = useMemo(() => moment().utc().add(24, 'hours').format('YYYY-MM-DDTHH:mm:ssZ'), []);
  const next48h = useMemo(() => moment().utc().add(48, 'hours').format('YYYY-MM-DDTHH:mm:ssZ'), []);
  const next72h = useMemo(() => moment().utc().add(72, 'hours').format('YYYY-MM-DDTHH:mm:ssZ'), []);
  const nextMonth = useMemo(() => moment().utc().add(29, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'), []);

  const [getArrivalData, { data: arrivals, ...arrivalsRequest }] = useGetPortArrivalsDataMutation();
  const debounceGetArrivalsHandler = useCallback(throttle(getArrivalData, 500), []);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);

  useEffect(() => {
    arrivalsRequest.reset();
  }, []);

  const [sorting, setSorting] = useState<PortArrivalsSortingInterface>(initialSort);
  const [etaVal, setEtaVal] = useState<DateTimeInterval>();
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebounce<string>(inputValue, 500);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState<PortArrivalsFilters>();
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(() => shipCallsColumns, []);

  useEffect(() => {
    if (curDate && nextMonth) {
      setEtaVal({
        from: curDate,
        to: nextMonth,
      });
    }
  }, [curDate, nextMonth]);

  const initialBody: TargetArrivalsSearchRequest = useMemo(
    () => ({
      filter: {
        fleetOnly: false,
        fleetGroup: [],
        type: [],
        country: [],
        eta: {
          from: curDate,
          to: nextMonth,
        },
      },
      order: initialSort,
      pagination: {
        offset: 0,
        limit: 20,
      },
    }),
    [curDate, nextMonth, initialSort],
  );

  const handleExport = () => {
    portKey &&
      getCSV({
        key: portKey,
        body: {
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            eta: etaVal || initialBody.filter.eta,
            fleetOnly: filters?.fleetOnly || false,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : undefined,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      })
        .unwrap()
        .then(handleFileDownload)
        .catch((error) => addAlert(error));
  };

  useEffect(() => {
    setCurrentPage(1);
    user &&
      debounceGetArrivalsHandler({
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            eta: etaVal || initialBody.filter.eta,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },
          pagination: {
            offset: currentOffset,
            limit: 20,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
        key: portKey,
      });
  }, [debouncedInputValue]);

  useEffect(() => {
    if (user) {
      debounceGetArrivalsHandler({
        key: portKey,
        body: {
          ...initialBody,

          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,

            eta: etaVal || initialBody.filter.eta,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },
          pagination: {
            offset: currentOffset,
            limit: 20,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      });
    }
  }, [currentOffset, user, language]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      debounceGetArrivalsHandler({
        key: portKey,
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            eta: etaVal || initialBody.filter.eta,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },

          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      });
    }
  }, [sorting]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setSorting(initialBody.order);

      debounceGetArrivalsHandler({
        key: portKey,
        body: {
          ...initialBody,
          filter: {
            fleetGroup: filters?.fleetGroupAll ? [] : filters?.fleetGroup,
            eta: etaVal || initialBody.filter.eta,
            fleetOnly: filters?.fleetOnly || false,
            term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
            type: filters?.typeAll ? [] : filters?.type,
            country: filters?.countryAll ? [] : filters?.country,
          },
        },
      });
    }
  }, [filters, etaVal]);

  const clickOutsideHandler = () => {
    setShowFilter(false);
  };
  useOnClickOutside(filterPanelRef, clickOutsideHandler);

  const handleDateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    switch (e.target?.value) {
      case '24h':
        setEtaVal({
          from: curDate,
          to: next24h,
        });
        break;
      case '48h':
        setEtaVal({
          from: curDate,
          to: next48h,
        });
        break;
      case '72h':
        setEtaVal({
          from: curDate,
          to: next72h,
        });
        break;
      case '30d':
        setEtaVal({
          from: curDate,
          to: nextMonth,
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div className={styles.table_header}>
        <Typo
          value={`${t('VesselCallsExpected')}${filters?.touched ? ` ${t('AppliedFilters')}:` : ':'} ${typeof arrivals?.total === 'number' ? arrivals.total : '–'}`}
          size="m"
        />
        <div className={styles.table_actions}>
          <div className={styles.form_select}>
            <select defaultValue={'30d'} onChange={handleDateChange} className={styles.dateSelect}>
              <option value="24h">{t('PortArrivalsDateOption_24h')}</option>
              <option value="48h">{t('PortArrivalsDateOption_48h')}</option>
              <option value="72h">{t('PortArrivalsDateOption_72h')}</option>
              <option value="30d">{t('PortArrivalsDateOption_30d')}</option>
            </select>
          </div>

          <Button variant="general" onClick={() => setShowFilter(true)} className={styles.icon_button}>
            {filters?.touched && <div className={styles.indicator} />}
            <Icon name="filter" />
            <Typo value={t('Filter')} variant="p" />
          </Button>

          <Button
            label={t('ButtonLabelExportSCV')}
            variant="general"
            className={styles.icon_button}
            onClick={handleExport}
            disabled={!handleExport}
          />

          <Input
            className={styles.table_search}
            type="search"
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('SearchPlaceholder')}
            value={inputValue}
          />
        </div>
      </div>

      {arrivalsRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {!arrivalsRequest.isLoading && arrivals?.results?.length === 0 && <p className={styles.no_data}>{t('NoResultsFound')}</p>}
      {arrivals?.results && arrivals.total && arrivals.results.length > 0 ? (
        <>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} columns={columns} data={arrivals.results} setSorting={setSorting} />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={arrivals.total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </>
      ) : null}

      {showFilter && (
        <PortArrivalsFilterPanel
          filters={filters}
          handleFilters={setFilters}
          innerRef={filterPanelRef}
          onClose={() => {
            setShowFilter(false);
          }}
        />
      )}
    </>
  );
};
